import { snackController } from '@/components/snack-bar/snack-bar-controller'
import { LockPeriods, Zero } from '@/constants'
import { FarmLPHandler } from '@/helpers/farmLP-handler'
import { walletStore } from '@/stores/wallet-store'
import { FixedNumber } from '@ethersproject/bignumber'
import { chunk } from 'lodash-es'
import { action, computed, IReactionDisposer, observable, reaction, when } from 'mobx'
import { asyncAction } from 'mobx-utils'
import { Subject, timer } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

export class FarmAdminViewModel {
  _disposers: IReactionDisposer[] = []
  private _unsubcrible = new Subject()

  @observable loading = false

  @observable tokenPerSecondInput = ''
  @observable tokenPerSecondSetting = false
  farmLPHandler?: FarmLPHandler

  @observable farmType = 'SB'
  @observable tab = 0
  @observable locktimeTab = 0
  @observable farmAddress = ''

  constructor() {
    this.loadData()
    this._disposers = [
      reaction(
        () => walletStore.account,
        () => {
          if (walletStore.isChainIdValid) this.farmLPHandler?.injectMetamask(walletStore.web3!)
        }
      )
    ]

    timer(0, 15000)
      .pipe(takeUntil(this._unsubcrible))
      .subscribe(async () => {
        this.getInfo()
      })
  }

  destroy() {
    this._unsubcrible.next()
    this._unsubcrible.complete()
    this._disposers.forEach(d => d())
  }

  getInfo() {
    this.getFarmLiquidityAndApy()
    this.getTokenBalanceInContract()
    this.getTotalLockedAmount()
    this.getUserLength()
    this.getLPInfo()
  }

  @observable lpInfo: any = {};
  @asyncAction *getLPInfo() {
    const lpInfo = yield this.farmLPHandler?.getLPInfo()
    this.lpInfo = lpInfo
  }

  @asyncAction *loadData() {
    this.loading = true
    try {
      const currentLocktime = Object.values(LockPeriods)[this.locktimeTab]
      const farmLPHandler = new FarmLPHandler(this.farmType, currentLocktime.value)
      this.farmLPHandler = farmLPHandler
      this.farmAddress = farmLPHandler.farmAddress

      yield farmLPHandler.load()
      yield Promise.all([this.getFarmLiquidityAndApy(), this.getPoolUserStakes()])

      this._disposers = [
        when(
          () => walletStore.connected,
          async () => {
            if (walletStore.chainId == process.env.VUE_APP_CHAIN_ID) {
              this.farmLPHandler?.injectMetamask(walletStore.web3!)
            }
          }
        )
      ]
    } catch (error) {
      console.error(error)
      snackController.commonError(error)
    } finally {
      this.loading = false
    }
  }

  @asyncAction *setTokenPerSecond() {
    this.tokenPerSecondSetting = true
    try {
      yield this.farmLPHandler?.setTokenPerSecond(this.tokenPerSecondInput, walletStore.account)
      yield this.farmLPHandler?.getTokenPerSecond()
      this.getFarmLiquidityAndApy()
      this.tokenPerSecondInput = ''
      this.estimateAPR = Zero
      snackController.success('Setting Token Per Second successfully')
    } catch (error) {
      console.error(error)
      snackController.commonError(error)
    } finally {
      this.tokenPerSecondSetting = false
    }
  }

  @observable tokenBalanceInContract = Zero;
  @asyncAction *getTokenBalanceInContract() {
    const tokenBalanceInContract = yield this.farmLPHandler?.getTokenBalanceInContract()
    this.tokenBalanceInContract = tokenBalanceInContract
  }

  @observable totalLockedAmount = Zero;
  @asyncAction *getTotalLockedAmount() {
    const total = yield this.farmLPHandler?.getTotalLockedAmount(0)
    this.totalLockedAmount = total
  }

  @observable userLength = 0;
  @asyncAction *getUserLength() {
    const userLength = yield this.farmLPHandler?.getUserLength(0)
    this.userLength = userLength
  }

  @observable poolUserStakes: any = [];
  @asyncAction *getPoolUserStakes() {
    const poolUserStakes = yield this.farmLPHandler?.getPoolUserStakes(0)
    this.poolUserStakes = poolUserStakes
  }

  @asyncAction *changeTab(tab) {
    this.tab = tab
    if (tab) this.farmType = 'STE'
    else this.farmType = 'SB'
    this.reset()
    yield this.loadData()
    this.getInfo()
  }

  @asyncAction *changeLocktimeTab(tab) {
    this.locktimeTab = tab
    this.tokenPerSecondInput = ''
    yield this.loadData()
    this.getInfo()
  }

  @action.bound reset() {
    this.tokenPerSecondInput = ''
    this.locktimeTab = 0
  }

  @observable currentAPR = Zero;
  @asyncAction *getFarmLiquidityAndApy() {
    const { apy } = yield this.farmLPHandler?.getFarmLiquidityAndApy()
    this.currentAPR = apy
  }

  @observable estimateAPR = Zero;
  @asyncAction *getEstimateAPR(tokenPerSecondInput = '') {
    const { apy } = yield this.farmLPHandler?.getFarmLiquidityAndApy(FixedNumber.from(tokenPerSecondInput))
    this.estimateAPR = apy
  }

  @action.bound changeTokenPerSecond(val) {
    this.tokenPerSecondInput = val
    if (this.validTokenPerSecondInput) {
      this.getEstimateAPR(val)
    } else {
      this.estimateAPR = Zero
    }
  }

  @computed get validTokenPerSecondInput() {
    try {
      FixedNumber.from(this.tokenPerSecondInput)
      return true
    } catch (error) {
      return false
    }
  }

  @computed get estimateTokenPerLockTime() {
    try {
      const currentLocktime = Object.values(LockPeriods)[this.locktimeTab]
      const lockTimeInSecond = FixedNumber.from(`${currentLocktime.value * 24 * 60 * 60}`)

      return FixedNumber.from(this.tokenPerSecondInput).mulUnsafe(lockTimeInSecond)
    } catch (error) {
      return Zero
    }
  }

  SENCOND_PER_YEAR = FixedNumber.from(`${60 * 60 * 24 * 365}`)
  @computed get estimateTokenPerYear() {
    try {
      return FixedNumber.from(this.tokenPerSecondInput).mulUnsafe(this.SENCOND_PER_YEAR)
    } catch (error) {
      return Zero
    }
  }

  @computed get estimateTokenStaked() {
    try {
      return this.totalLockedAmount.mulUnsafe(this.lpInfo.tokenBalance).divUnsafe(this.lpInfo.totalSupplyLP)
    } catch (error) {
      return Zero
    }
  }
  @computed get estimateUsdStaked() {
    try {
      return this.totalLockedAmount.mulUnsafe(this.lpInfo.usdBalance).divUnsafe(this.lpInfo.totalSupplyLP)
    } catch (error) {
      return Zero
    }
  }
}
